import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Trash2,
  Mail,
  Slack,
  Send,
  Bell,
  History,
  CheckCircle2,
  XCircle,
  X,
  AlertTriangle,
} from 'lucide-react';
import { useDialog } from '@shared/hooks/useDialog';
import { useUser } from '@shared/hooks/useUser';
import { Card, Badge, Button, Input } from '@shared/components/ui';
import { getLogLevelTone, getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { toneIconBgClasses } from '@shared/utils/statusTone';
import { BackupAlertState } from '@features/backups/types/alert-state';
import { translateHistorySubject } from '../utils/translateHistorySubject';

interface Notification {
  id: string;
  type: string;
  channel: string;
  destination: string;
  is_enabled: boolean;
  severity: string;
  created_at: string;
}

interface NotificationHistory {
  id: string;
  type?: string;
  channel?: string;
  status: string;
  subject: string;
  body: string;
  sent_at: string;
  error_message: string;
}

export default function Notifications() {
  const { t, i18n } = useTranslation(['notifications', 'common']);
  const locale = i18n.resolvedLanguage || i18n.language;
  const { showConfirm } = useDialog();
  const { user } = useUser();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [history, setHistory] = useState<NotificationHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newNotification, setNewNotification] = useState({
    type: 'BACKUP_SUCCESS',
    channel: 'EMAIL',
    destination: '',
    is_enabled: true,
  });
  const [error, setError] = useState<string | null>(null);
  const [channelLoadError, setChannelLoadError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testingWebhook, setTestingWebhook] = useState<string | null>(null);
  const [filterChannel, setFilterChannel] = useState<string>('');
  const [filterStatus] = useState<string>('');
  const [selectedNotification, setSelectedNotification] = useState<NotificationHistory | null>(
    null
  );
  const failureDialogRef = useRef<HTMLDivElement>(null);
  const failureDialogOpenerRef = useRef<HTMLElement | null>(null);
  const [alertStates, setAlertStates] = useState<BackupAlertState[]>([]);

  const token = localStorage.getItem('auth_token');

  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchAlertStates();
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchHistory();
  }, [filterChannel, filterStatus, user]);

  useEffect(() => {
    if (!selectedNotification) return;

    const dialog = failureDialogRef.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog?.querySelector<HTMLElement>('button')?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setSelectedNotification(null);
        return;
      }

      if (event.key !== 'Tab' || !dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      failureDialogOpenerRef.current?.focus();
      failureDialogOpenerRef.current = null;
    };
  }, [selectedNotification]);

  const fetchNotifications = async () => {
    setLoading(true);
    setChannelLoadError(null);
    try {
      const res = await fetch('/api/v1/notifications', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Request failed');
      const data = await res.json();
      setNotifications(data.notifications || []);
    } catch {
      setChannelLoadError(t('errors.fetchFailed'));
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const params = new URLSearchParams();
      if (filterChannel) params.append('channel', filterChannel);
      if (filterStatus) params.append('status', filterStatus);

      const res = await fetch(`/api/v1/notifications/history?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Request failed');
      const data = await res.json();
      setHistory(data.history || []);
    } catch {
      setHistory([]);
      setHistoryError(t('errors.historyFetchFailed'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchAlertStates = async () => {
    try {
      const res = await fetch('/api/v1/backups/alert-states', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setAlertStates(data.states || []);
      }
    } catch {
      console.error('Failed to fetch alert states');
    }
  };

  const handleCreateNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch('/api/v1/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newNotification),
      });

      if (!res.ok) throw new Error((await res.json()).error || t('errors.createFailed'));

      setSuccess(t('success.created'));
      setShowCreateModal(false);
      setNewNotification({
        type: 'BACKUP_SUCCESS',
        channel: 'EMAIL',
        destination: '',
        is_enabled: true,
      });
      fetchNotifications();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteNotification = async (id: string) => {
    const confirmed = await showConfirm(t('confirmDelete'), t('deletePrompt'), 'warning');
    if (!confirmed) return;
    try {
      await fetch(`/api/v1/notifications/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess(t('success.deleted'));
      fetchNotifications();
    } catch {
      setError(t('errors.deleteFailed'));
    }
  };

  const handleToggleEnabled = async (id: string, currentState: boolean) => {
    try {
      await fetch(`/api/v1/notifications/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ is_enabled: !currentState }),
      });
      fetchNotifications();
    } catch {
      setError(t('errors.updateFailed'));
    }
  };

  const handleTestWebhook = async (channel: string, destination: string) => {
    if (channel === 'EMAIL') {
      setError(t('errors.emailTestNote'));
      return;
    }
    setTestingWebhook(destination);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch('/api/v1/notifications/testwebhook', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ channel, destination }),
      });
      if (!res.ok) throw new Error((await res.json()).error || t('errors.testFailed'));
      setSuccess(t('success.testSent'));
      fetchHistory();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setTestingWebhook(null);
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel.toUpperCase()) {
      case 'EMAIL':
        return <Mail size={18} />;
      case 'SLACK':
        return <Slack size={18} />;
      default:
        return <Bell size={18} />;
    }
  };

  const formatUnit = (value: number, unit: 'hour' | 'minute') =>
    new Intl.NumberFormat(locale, { style: 'unit', unit, unitDisplay: 'short' }).format(value);
  const formatMinutes = (minutes: number) => formatUnit(minutes, 'minute');

  const formatDuration = (seconds: number) => {
    if (!seconds || seconds < 0) return t('alertStates.zeroMinutes');
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0 && minutes > 0)
      return `${formatUnit(hours, 'hour')} ${formatUnit(minutes, 'minute')}`;
    if (hours > 0) return formatUnit(hours, 'hour');
    if (minutes > 0) return formatUnit(minutes, 'minute');
    return t('alertStates.lessThanMinute');
  };

  const formatDateTime = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'medium' }).format(date);
  };

  const translateEventType = (value: string) => {
    const eventKeys: Record<string, string> = {
      BACKUP_SUCCESS: 'backupSuccess',
      BACKUP_FAILURE: 'backupFailure',
      RESTORE_SUCCESS: 'restoreSuccess',
      AGENT_OFFLINE: 'agentOffline',
    };
    const eventKey = eventKeys[value.toUpperCase()];
    return eventKey ? t(`modal.events.${eventKey}`) : value.replace(/_/g, ' ').toLowerCase();
  };

  const translateEnum = (group: 'statuses' | 'severities', value: string) => {
    const key = value.toLowerCase();
    const translated = t(`${group}.${key}`);
    return translated === `${group}.${key}` ? value : translated;
  };

  const translateChannel = (channel: string | undefined) => {
    if (!channel) return '';
    const key = `history.${channel.toLowerCase()}`;
    const translated = t(key);
    return translated === key ? channel : translated;
  };

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-300">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black text-slate-900 dark:text-white tracking-tight">
              {t('title')}
            </h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('subtitle')}
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} size="lg" className="shadow-brand-200">
            <Plus className="w-5 h-5 mr-2" strokeWidth={3} /> {t('modal.title')}
          </Button>
        </div>

        {error && (
          <div className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 p-4 rounded-xl flex items-center gap-3 text-state-danger-light dark:text-state-danger-dark font-bold animate-in slide-in-from-top duration-300">
            <XCircle size={20} className="text-state-danger-light dark:text-state-danger-dark" />{' '}
            {error}
          </div>
        )}
        {success && (
          <div className="bg-state-ok-bg-light dark:bg-state-ok-bg-dark border border-state-ok-light/20 dark:border-state-ok-dark/20 p-4 rounded-xl flex items-center gap-3 text-state-ok-light dark:text-state-ok-dark font-bold animate-in slide-in-from-top duration-300">
            <CheckCircle2 size={20} className="text-state-ok-light dark:text-state-ok-dark" />{' '}
            {success}
          </div>
        )}

        {/* Alert Throttling Info */}
        <div className="bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 dark:border-state-warn-dark/20 p-4 rounded-xl flex items-start gap-3">
          <AlertTriangle
            className="text-state-warn-light dark:text-state-warn-dark shrink-0 mt-0.5"
            size={20}
          />
          <div className="text-sm text-state-warn-light dark:text-state-warn-dark">
            <p className="font-bold mb-1">{t('alertThrottling.title')}</p>
            <p className="text-xs">{t('alertThrottling.description')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Configured Notifications */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="overflow-hidden">
              <div className="p-6 bg-surface-2-light dark:bg-surface-2-dark border-b border-surface-border-light dark:border-surface-border-dark flex justify-between items-center">
                <h2 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <Bell className="text-brand-600 dark:text-brand-400" /> {t('channels.title')}
                </h2>
                <Badge variant="neutral">
                  {loading || channelLoadError ? '—' : notifications.length}
                </Badge>
              </div>
              <div className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                {loading ? (
                  <div
                    className="p-12 flex justify-center"
                    role="status"
                    aria-label={t('channels.loading')}
                    aria-live="polite"
                  >
                    <div className="animate-spin h-8 w-8 border-b-2 border-brand-600 rounded-full"></div>
                  </div>
                ) : channelLoadError ? (
                  <div className="p-8 text-center">
                    <p
                      role="alert"
                      className="mb-3 font-medium text-state-danger-light dark:text-state-danger-dark"
                    >
                      {channelLoadError}
                    </p>
                    <Button type="button" variant="secondary" onClick={fetchNotifications}>
                      {t('channels.retry')}
                    </Button>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-12 text-center text-text-subtle-light dark:text-text-subtle-dark font-medium">
                    {t('channels.empty')}
                  </div>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 group hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div
                          className={`p-3 rounded-2xl ${
                            notif.channel === 'EMAIL'
                              ? 'bg-state-info-bg-light dark:bg-state-info-bg-dark text-state-info-light dark:text-state-info-dark'
                              : 'bg-accent-soft-light dark:bg-accent-soft-dark text-accent-text-light dark:text-accent-text-dark'
                          }`}
                        >
                          {getChannelIcon(notif.channel)}
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <h3 className="font-bold text-slate-900 dark:text-white capitalize">
                              {translateEventType(notif.type)}
                            </h3>
                            <Badge
                              variant={toneToBadgeVariant[getLogLevelTone(notif.severity)]}
                              className="text-[10px] uppercase"
                            >
                              {translateEnum('severities', notif.severity)}
                            </Badge>
                          </div>
                          <p className="text-xs text-text-muted-light dark:text-text-muted-dark font-medium font-mono">
                            {notif.destination}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="mr-4 flex min-h-11 cursor-pointer items-center gap-2">
                          <input
                            type="checkbox"
                            checked={notif.is_enabled}
                            onChange={() => handleToggleEnabled(notif.id, notif.is_enabled)}
                            className="h-5 w-5 rounded border-border-strong-light dark:border-border-strong-dark text-brand-600 dark:text-brand-400 focus:ring-brand-500 dark:focus:ring-brand-400"
                          />
                          <span className="text-xs font-bold text-text-muted-light dark:text-text-muted-dark uppercase">
                            {t('channels.active')}
                          </span>
                        </label>
                        {notif.channel !== 'EMAIL' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-11"
                            onClick={() => handleTestWebhook(notif.channel, notif.destination)}
                            isLoading={testingWebhook === notif.destination}
                          >
                            <Send size={14} className="mr-1" /> {t('channels.test')}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-11 w-11 text-text-subtle-light dark:text-text-subtle-dark hover:text-state-danger-light dark:hover:text-state-danger-dark"
                          aria-label={t('deleteChannel', { destination: notif.destination })}
                          onClick={() => handleDeleteNotification(notif.id)}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* History */}
            <Card className="overflow-hidden">
              <div className="p-4 sm:p-6 bg-surface-2-light dark:bg-surface-2-dark border-b border-surface-border-light dark:border-surface-border-dark flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <History className="text-state-info-light dark:text-state-info-dark" />{' '}
                  {t('history.title')}
                </h2>
                <div className="flex gap-2">
                  <select
                    value={filterChannel}
                    onChange={(e) => setFilterChannel(e.target.value)}
                    aria-label={t('history.filterChannels')}
                    className="min-h-11 max-w-full rounded-lg border border-surface-border-light bg-surface-card-light px-3 text-xs font-bold text-text-muted-light focus:ring-2 focus:ring-brand-500 dark:border-surface-border-dark dark:bg-surface-card-dark dark:text-text-muted-dark dark:focus:ring-brand-400"
                  >
                    <option value="">{t('history.filterChannels')}</option>
                    <option value="EMAIL">{t('history.email')}</option>
                    <option value="SLACK">{t('history.slack')}</option>
                  </select>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-surface-2-light dark:bg-surface-2-dark border-b border-surface-border-light dark:border-surface-border-dark">
                    <tr>
                      <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                        {t('history.status')}
                      </th>
                      <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark">
                        {t('history.subject')}
                      </th>
                      <th className="p-4 text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark text-right">
                        {t('history.sent')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                    {historyLoading && (
                      <tr>
                        <td colSpan={3} role="status" className="p-12 text-center font-medium">
                          {t('history.loading')}
                        </td>
                      </tr>
                    )}
                    {!historyLoading && historyError && (
                      <tr>
                        <td colSpan={3} className="p-8 text-center">
                          <p
                            role="alert"
                            className="mb-3 font-medium text-state-danger-light dark:text-state-danger-dark"
                          >
                            {historyError}
                          </p>
                          <Button type="button" variant="secondary" onClick={fetchHistory}>
                            {t('history.retry')}
                          </Button>
                        </td>
                      </tr>
                    )}
                    {!historyLoading &&
                      !historyError &&
                      history.map((item) => {
                        const translatedSubject = translateHistorySubject(item.subject, t);
                        const isFailed = item.status === 'FAILED';

                        return (
                          <tr key={item.id}>
                            <td className="p-4">
                              {isFailed ? (
                                <button
                                  type="button"
                                  aria-label={t('history.showFailureDetails', {
                                    subject: translatedSubject,
                                  })}
                                  aria-haspopup="dialog"
                                  className="inline-flex min-h-11 items-center rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                                  onClick={(event) => {
                                    failureDialogOpenerRef.current = event.currentTarget;
                                    setSelectedNotification(item);
                                  }}
                                >
                                  <Badge
                                    variant={toneToBadgeVariant[getStatusTone(item.status)]}
                                    className="text-[10px]"
                                  >
                                    {translateEnum('statuses', item.status)}
                                  </Badge>
                                </button>
                              ) : (
                                <Badge
                                  variant={toneToBadgeVariant[getStatusTone(item.status)]}
                                  className="text-[10px]"
                                >
                                  {translateEnum('statuses', item.status)}
                                </Badge>
                              )}
                            </td>
                            <td className="p-4">
                              <div
                                title={translatedSubject}
                                className="max-w-[200px] whitespace-normal break-words text-sm font-bold text-slate-700 dark:text-slate-300"
                              >
                                {translatedSubject}
                              </div>
                              <div className="text-[10px] text-text-subtle-light dark:text-text-subtle-dark font-mono">
                                {translateChannel(item.channel)}
                              </div>
                            </td>
                            <td className="p-4 text-right text-xs font-medium text-text-muted-light dark:text-text-muted-dark">
                              {formatDateTime(item.sent_at)}
                            </td>
                          </tr>
                        );
                      })}
                    {!historyLoading && !historyError && history.length === 0 && (
                      <tr>
                        <td
                          colSpan={3}
                          className="p-12 text-center text-text-subtle-light dark:text-text-subtle-dark font-medium"
                        >
                          {t('history.empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="p-6 bg-surface-2-light dark:bg-surface-2-dark overflow-hidden relative">
              <div className="absolute top-0 right-0 p-8 opacity-10">
                <Bell size={120} />
              </div>
              <h4 className="text-sm font-black uppercase tracking-widest text-text-muted-light dark:text-text-subtle-dark mb-4">
                {t('guide.title')}
              </h4>
              <div className="space-y-4 relative z-10">
                <GuideItem
                  icon={<Mail size={14} />}
                  title={t('guide.email.title')}
                  text={t('guide.email.text')}
                />
                <GuideItem
                  icon={<Slack size={14} />}
                  title={t('guide.slack.title')}
                  text={t('guide.slack.text')}
                />
              </div>
            </Card>

            {/* Alert States Dashboard */}
            <Card className="overflow-hidden">
              <div className="p-6 bg-surface-2-light dark:bg-surface-2-dark border-b border-surface-border-light dark:border-surface-border-dark flex justify-between items-center">
                <h2 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <AlertTriangle className="text-state-warn-light dark:text-state-warn-dark" />{' '}
                  {t('alertStates.title')}
                </h2>
                <Badge variant="neutral">{alertStates.length}</Badge>
              </div>
              <div className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                {alertStates.length === 0 ? (
                  <div className="p-12 text-center text-text-subtle-light dark:text-text-subtle-dark font-medium">
                    {t('alertStates.empty')}
                  </div>
                ) : (
                  alertStates.map((state) => (
                    <button
                      type="button"
                      key={state.backup_config_id}
                      className="w-full p-4 text-left hover:bg-surface-2-light dark:hover:bg-surface-2-dark transition-colors cursor-pointer"
                      onClick={() =>
                        navigate(`/backups/configs/${state.backup_config_id}/alert-state`)
                      }
                    >
                      {/* First row: Name and status */}
                      <div className="flex items-start gap-3 mb-2">
                        <div
                          className={`p-2 rounded-xl ${toneIconBgClasses[getStatusTone(state.alert_state)]}`}
                        >
                          {getStatusTone(state.alert_state) === 'ok' ? (
                            <CheckCircle2 size={16} />
                          ) : (
                            <AlertTriangle size={16} />
                          )}
                        </div>
                        <h3 className="min-w-0 flex-1 break-words font-bold text-slate-900 dark:text-white">
                          {state.backup_name || t('alertStates.unknownBackup')}
                        </h3>
                        <Badge
                          variant={toneToBadgeVariant[getStatusTone(state.alert_state)]}
                          className="text-[10px]"
                        >
                          {translateEnum('statuses', state.alert_state)}
                        </Badge>
                      </div>

                      {/* Second row: Data */}
                      <div className="flex flex-col items-start gap-y-1 pl-9 text-xs text-text-muted-light dark:text-text-muted-dark sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="font-medium">
                            {t('alertStates.grace')}:{' '}
                            <strong className="text-slate-700 dark:text-slate-300">
                              {formatMinutes(state.grace_period_minutes)}
                            </strong>
                          </span>
                          <span className="font-medium">
                            {t('alertStates.throttle')}:{' '}
                            <strong className="text-slate-700 dark:text-slate-300">
                              {formatMinutes(state.throttle_interval_minutes)}
                            </strong>
                          </span>
                        </div>
                        <div className="break-words sm:text-right">
                          {state.alert_state !== 'OK' ? (
                            <span>
                              {t('alertStates.failingFor')}{' '}
                              {formatDuration(state.total_downtime_seconds)} •{' '}
                              {state.consecutive_failures} {t('alertStates.failures')}
                            </span>
                          ) : state.last_successful_run_at ? (
                            <span>
                              {t('alertStates.lastSuccess')}{' '}
                              {formatDateTime(state.last_successful_run_at)}
                            </span>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* Modals */}
        {showCreateModal && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="notification-modal-title"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in"
          >
            <Card className="w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto shadow-elevated p-5 sm:p-8">
              <div className="flex justify-between items-center mb-6">
                <h2
                  id="notification-modal-title"
                  className="text-2xl font-black text-slate-900 dark:text-white tracking-tight"
                >
                  {t('modal.title')}
                </h2>
                <button
                  type="button"
                  aria-label={t('common:actions.close')}
                  className="min-h-11 min-w-11 inline-flex items-center justify-center"
                  onClick={() => setShowCreateModal(false)}
                >
                  <X className="text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-white transition-colors" />
                </button>
              </div>
              <form onSubmit={handleCreateNotification} className="space-y-5">
                <div>
                  <label
                    htmlFor="notification-event-type"
                    className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                  >
                    {t('modal.eventType')}
                  </label>
                  <select
                    id="notification-event-type"
                    value={newNotification.type}
                    onChange={(e) =>
                      setNewNotification({ ...newNotification, type: e.target.value })
                    }
                    className="w-full min-h-11 px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 outline-none"
                  >
                    <option value="BACKUP_SUCCESS">{t('modal.events.backupSuccess')}</option>
                    <option value="BACKUP_FAILURE">{t('modal.events.backupFailure')}</option>
                    <option value="RESTORE_SUCCESS">{t('modal.events.restoreSuccess')}</option>
                    <option value="AGENT_OFFLINE">{t('modal.events.agentOffline')}</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="notification-channel"
                    className="text-xs font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-2 block"
                  >
                    {t('modal.channel')}
                  </label>
                  <select
                    id="notification-channel"
                    value={newNotification.channel}
                    onChange={(e) =>
                      setNewNotification({ ...newNotification, channel: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold text-slate-900 dark:text-white focus:ring-2 focus:ring-brand-500 dark:focus:ring-brand-400 outline-none"
                  >
                    <option value="EMAIL">{t('modal.channels.email')}</option>
                    <option value="SLACK">{t('modal.channels.slack')}</option>
                  </select>
                </div>
                <Input
                  label={
                    newNotification.channel === 'EMAIL'
                      ? t('modal.emailLabel')
                      : t('modal.webhookLabel')
                  }
                  type={newNotification.channel === 'EMAIL' ? 'email' : 'url'}
                  value={newNotification.destination}
                  onChange={(e) =>
                    setNewNotification({ ...newNotification, destination: e.target.value })
                  }
                  required
                />
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="secondary"
                    type="button"
                    className="flex-1"
                    onClick={() => setShowCreateModal(false)}
                  >
                    {t('common:actions.cancel')}
                  </Button>
                  <Button className="flex-1" type="submit">
                    {t('modal.createButton')}
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}

        {selectedNotification && (
          <div
            ref={failureDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="notification-error-title"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in"
          >
            <Card className="w-full max-w-lg max-h-[calc(100dvh-2rem)] overflow-y-auto shadow-elevated p-5 sm:p-8">
              <div className="flex justify-between items-center mb-6">
                <h2
                  id="notification-error-title"
                  className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-2"
                >
                  <XCircle className="text-state-danger-light dark:text-state-danger-dark" />{' '}
                  {t('errorModal.title')}
                </h2>
                <button
                  type="button"
                  aria-label={t('common:actions.close')}
                  className="min-h-11 min-w-11 inline-flex items-center justify-center"
                  onClick={() => setSelectedNotification(null)}
                >
                  <X className="text-text-subtle-light dark:text-text-subtle-dark hover:text-slate-600 dark:hover:text-white transition-colors" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="p-4 bg-state-danger-bg-light dark:bg-state-danger-bg-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl text-sm text-state-danger-light dark:text-state-danger-dark font-mono break-words">
                  {selectedNotification.error_message}
                </div>
                <div className="grid grid-cols-2 gap-4 text-xs font-bold uppercase tracking-wider text-text-muted-light dark:text-text-muted-dark">
                  <div>
                    {t('errorModal.subject')}:{' '}
                    <span className="text-slate-900 dark:text-white lowercase block mt-1">
                      {translateHistorySubject(selectedNotification.subject, t)}
                    </span>
                  </div>
                  <div>
                    {t('errorModal.sent')}:{' '}
                    <span className="text-slate-900 dark:text-white lowercase block mt-1">
                      {formatDateTime(selectedNotification.sent_at)}
                    </span>
                  </div>
                </div>
                <Button
                  variant="secondary"
                  className="w-full mt-4"
                  onClick={() => setSelectedNotification(null)}
                >
                  {t('common.close')}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </>
  );
}

function GuideItem({ icon, title, text }: any) {
  return (
    <div className="flex gap-3">
      <div className="mt-0.5 text-text-muted-light dark:text-text-muted-dark">{icon}</div>
      <div>
        <p className="text-sm font-black text-slate-800 dark:text-slate-200">{title}</p>
        <p className="text-xs text-text-muted-light dark:text-text-muted-dark font-medium leading-relaxed">
          {text}
        </p>
      </div>
    </div>
  );
}
